import { useCallback, useEffect, useState } from "react";
import type { EventPublicDTO } from "@wedding/shared-types";
import { getEventPublic, getEventMedia, verifyEventPassword } from "../api/events.js";
import { ApiClientError } from "../api/client.js";
import { setUploaderName } from "../lib/uploaderName.js";

export type EventAccessState =
  | { status: "loading" }
  | { status: "not-found" }
  | { status: "unavailable"; event: EventPublicDTO }
  | { status: "needs-password"; event: EventPublicDTO }
  | { status: "ready"; event: EventPublicDTO };

export function useEventAccess(token: string) {
  const [state, setState] = useState<EventAccessState>({ status: "loading" });
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    try {
      const event = await getEventPublic(token);
      if (!event.isActive || event.isExpired) {
        setState({ status: "unavailable", event });
        return;
      }
      // The session cookie is HttpOnly (unreadable from JS by design), so
      // whether the guest is already authenticated is determined by probing
      // a cheap authenticated endpoint rather than inspecting a cookie.
      try {
        await getEventMedia(token, { limit: 1 });
        setState({ status: "ready", event });
      } catch (err) {
        if (err instanceof ApiClientError && err.status === 401) {
          setState({ status: "needs-password", event });
        } else {
          throw err;
        }
      }
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 404) {
        setState({ status: "not-found" });
      } else {
        setState({ status: "not-found" });
      }
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const verify = useCallback(
    async (password: string, name: string) => {
      setVerifying(true);
      setVerifyError(null);
      try {
        await verifyEventPassword(token, password);
        setUploaderName(name);
        await load();
      } catch (err) {
        setVerifyError(err instanceof ApiClientError ? err.message : "Une erreur est survenue");
      } finally {
        setVerifying(false);
      }
    },
    [token, load],
  );

  return { state, verify, verifying, verifyError };
}
